/**
 * Product Page JavaScript Module - EVSTORE
 * 
 * Features:
 * - Image zoom (hover/lightbox)
 * - Variant switching with price/image updates
 * - AJAX Add to Cart
 * - Gallery slider controls
 * - Quantity stepper
 */

class ProductPage {
    constructor(container) {
        this.container = container;
        this.productId = container.dataset.productId;
        this.productData = null;
        this.currentVariant = null;

        this.selectors = {
            form: '[data-product-form]',
            variantInput: '[data-variant-id]',
            optionInputs: '[data-option-input], [data-option-select]',
            price: '[data-price]',
            comparePrice: '[data-compare-price]',
            addToCartBtn: '[data-add-to-cart]',
            buyNowBtn: '[data-buy-now]',
            quantityInput: '[data-quantity-input]',
            quantityMinus: '[data-quantity-minus]',
            quantityPlus: '[data-quantity-plus]',
            galleryMain: '[data-gallery-main]',
            galleryThumbs: '.product-gallery__thumb',
            gallerySlides: '.product-gallery__slide',
            galleryDots: '.product-gallery__dot',
            galleryPrev: '[data-gallery-prev]',
            galleryNext: '[data-gallery-next]',
            zoomImages: '[data-zoom]',
            stockIndicator: '[data-stock-indicator]',
            tabs: '[data-tab]',
            tabToggle: '[data-tab-toggle]'
        };

        this.init();
    }

    async init() {
        await this.loadProductData();
        this.bindEvents();
        this.initZoom();
        this.initGallery();
        this.initStickyBar();
        this.initHoverZoom();
    }

    async loadProductData() {
        try {
            const response = await fetch(`${window.location.pathname}.js`);
            if (response.ok) {
                this.productData = await response.json();
                this.currentVariant = this.productData.variants.find(v => v.available) || this.productData.variants[0];
            }
        } catch (error) {
            console.warn('Could not load product data:', error);
        }
    }

    bindEvents() {
        // Variant option changes
        this.container.querySelectorAll(this.selectors.optionInputs).forEach(input => {
            input.addEventListener('change', (e) => this.onOptionChange(e));
        });

        // Quantity buttons
        this.container.querySelector(this.selectors.quantityMinus)?.addEventListener('click', () => this.updateQuantity(-1));
        this.container.querySelector(this.selectors.quantityPlus)?.addEventListener('click', () => this.updateQuantity(1));

        // Add to cart form
        const form = this.container.querySelector(this.selectors.form);
        form?.addEventListener('submit', (e) => this.onFormSubmit(e));

        // Tab toggles
        this.container.querySelectorAll(this.selectors.tabToggle).forEach(toggle => {
            toggle.addEventListener('click', () => {
                const tab = toggle.closest(this.selectors.tabs);
                tab?.classList.toggle('is-open');
            });
        });

        // Gallery thumbnails
        this.container.querySelectorAll(this.selectors.galleryThumbs).forEach(thumb => {
            thumb.addEventListener('click', () => this.onThumbClick(thumb));
        });

        // Slider navigation
        this.container.querySelector(this.selectors.galleryPrev)?.addEventListener('click', () => this.slideGallery(-1));
        this.container.querySelector(this.selectors.galleryNext)?.addEventListener('click', () => this.slideGallery(1));

        this.container.querySelectorAll(this.selectors.galleryDots).forEach((dot, index) => {
            dot.addEventListener('click', () => this.goToSlide(index));
        });

        // Copy link button
        this.container.querySelector('[data-copy-link]')?.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(window.location.href);
                this.showNotification('Link copied to clipboard!');
            } catch (err) {
                console.error('Failed to copy link');
            }
        });
    }

    onOptionChange(event) {
        const input = event.target;

        // Update visual state for radio buttons
        if (input.type === 'radio') {
            const container = input.closest('.product-page__option-buttons, .product-page__option-swatches');
            container?.querySelectorAll('label').forEach(l => l.classList.remove('is-selected'));
            input.closest('label')?.classList.add('is-selected');
        }

        // Find matching variant
        const selectedOptions = this.getSelectedOptions();
        const variant = this.findVariantByOptions(selectedOptions);

        if (variant) {
            this.updateVariant(variant);
        }
    }

    getSelectedOptions() {
        const options = [];

        this.container.querySelectorAll(this.selectors.optionInputs).forEach(input => {
            if (input.tagName === 'SELECT') {
                options.push(input.value);
            } else if (input.type === 'radio' && input.checked) {
                options.push(input.value);
            }
        });

        return options;
    }

    findVariantByOptions(options) {
        if (!this.productData) return null;

        return this.productData.variants.find(variant => {
            return options.every((option, index) => variant[`option${index + 1}`] === option);
        });
    }

    updateVariant(variant) {
        this.currentVariant = variant;

        // Update hidden variant input
        const variantInput = this.container.querySelector(this.selectors.variantInput);
        if (variantInput) {
            variantInput.value = variant.id;
        }

        // Update price
        this.updatePrice(variant);

        // Update stock indicator
        this.updateStockIndicator(variant);

        // Update add to cart button
        this.updateAddToCartButton(variant);

        // Update gallery image if variant has featured image
        if (variant.featured_media) {
            this.updateGalleryImage(variant.featured_media);
        }

        // Update option value labels
        this.updateOptionValueLabels(variant);

        // Update URL
        this.updateURL(variant);
    }

    updateOptionValueLabels(variant) {
        const optionContainers = this.container.querySelectorAll('.product-page__option');
        optionContainers.forEach((container, index) => {
            const valueEl = container.querySelector('.product-page__option-value');
            if (valueEl) {
                const value = variant[`option${index + 1}`];
                valueEl.textContent = value ? `: ${value}` : '';
            }
        });
    }

    updatePrice(variant) {
        const priceEl = this.container.querySelector(this.selectors.price);
        const comparePriceEl = this.container.querySelector(this.selectors.comparePrice);

        if (priceEl) {
            priceEl.textContent = this.formatMoney(variant.price);
        }

        if (comparePriceEl) {
            if (variant.compare_at_price > variant.price) {
                comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
                comparePriceEl.style.display = '';
            } else {
                comparePriceEl.style.display = 'none';
            }
        }
    }

    updateStockIndicator(variant) {
        const stockEl = this.container.querySelector(this.selectors.stockIndicator);
        if (!stockEl) return;

        const badge = stockEl.querySelector('.product-page__stock-badge');
        if (!badge) return;

        badge.classList.remove('product-page__stock-badge--in', 'product-page__stock-badge--low', 'product-page__stock-badge--out');

        if (!variant.available) {
            badge.classList.add('product-page__stock-badge--out');
            badge.textContent = 'Out of Stock';
        } else if (variant.inventory_quantity && variant.inventory_quantity <= 5) {
            badge.classList.add('product-page__stock-badge--low');
            badge.textContent = `Only ${variant.inventory_quantity} left!`;
        } else {
            badge.classList.add('product-page__stock-badge--in');
            badge.textContent = 'In Stock';
        }
    }

    updateAddToCartButton(variant) {
        const addBtn = this.container.querySelector(this.selectors.addToCartBtn);
        const buyNowBtn = this.container.querySelector(this.selectors.buyNowBtn);

        if (addBtn) {
            const labelAdd = addBtn.dataset.labelAdd || 'Add to Cart';
            const labelSoldOut = addBtn.dataset.labelSoldOut || 'Sold Out';
            if (variant.available) {
                addBtn.disabled = false;
                addBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          ${labelAdd}
        `;
            } else {
                addBtn.disabled = true;
                addBtn.textContent = labelSoldOut;
            }
        }

        if (buyNowBtn) {
            buyNowBtn.disabled = !variant.available;
        }
    }

    updateGalleryImage(media) {
        // For grid layout with thumbnails
        const thumb = this.container.querySelector(`[data-media-id="${media.id}"]`);
        if (thumb) {
            this.onThumbClick(thumb);
        }

        // For slider layout
        const slides = this.container.querySelectorAll(this.selectors.gallerySlides);
        slides.forEach((slide, index) => {
            if (slide.dataset.mediaId === String(media.id)) {
                this.goToSlide(index);
            }
        });
    }

    updateURL(variant) {
        if (!history.replaceState) return;

        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
    }

    updateQuantity(delta) {
        const input = this.container.querySelector(this.selectors.quantityInput);
        if (!input) return;

        const current = parseInt(input.value) || 1;
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 99;

        const newValue = Math.min(Math.max(current + delta, min), max);
        input.value = newValue;
    }

    async onFormSubmit(event) {
        event.preventDefault();

        const form = event.target;
        const addBtn = form.querySelector(this.selectors.addToCartBtn);

        if (!addBtn || addBtn.disabled) return;

        // Set loading state
        const originalContent = addBtn.innerHTML;
        addBtn.disabled = true;
        addBtn.innerHTML = `
      <svg class="spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Adding...
    `;

        try {
            const formData = new FormData(form);

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const item = await response.json();

                // Show success
                addBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Added!
        `;

                // Update cart count
                this.updateCartCount();

                // Show notification
                this.showNotification(`${item.title} added to cart!`);

                // Reset button after delay
                setTimeout(() => {
                    addBtn.innerHTML = originalContent;
                    addBtn.disabled = false;
                }, 2000);

            } else {
                const error = await response.json();
                throw new Error(error.message || 'Could not add to cart');
            }

        } catch (error) {
            console.error('Add to cart error:', error);
            this.showNotification(error.message || 'Error adding to cart', 'error');
            addBtn.innerHTML = originalContent;
            addBtn.disabled = false;
        }
    }

    async updateCartCount() {
        try {
            const response = await fetch('/cart.js');
            if (response.ok) {
                const cart = await response.json();
                const countEl = document.querySelector('[data-cart-count]');
                if (countEl) {
                    countEl.textContent = cart.item_count;
                    countEl.style.display = cart.item_count > 0 ? '' : 'none';
                }
            }
        } catch (error) {
            console.error('Failed to update cart count:', error);
        }
    }

    // Gallery Methods
    initGallery() {
        this.currentSlide = 0;
        this.slides = this.container.querySelectorAll(this.selectors.gallerySlides);
        this.dots = this.container.querySelectorAll(this.selectors.galleryDots);
    }

    onThumbClick(thumb) {
        const mainImage = this.container.querySelector('#main-product-image');
        if (mainImage && thumb.dataset.imageUrl) {
            mainImage.removeAttribute('srcset');
            mainImage.src = thumb.dataset.imageUrl;
            if (thumb.dataset.zoomUrl) {
                mainImage.dataset.zoom = thumb.dataset.zoomUrl;
            }
        }

        this.container.querySelectorAll(this.selectors.galleryThumbs).forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
    }

    slideGallery(direction) {
        const newIndex = (this.currentSlide + direction + this.slides.length) % this.slides.length;
        this.goToSlide(newIndex);
    }

    goToSlide(index) {
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        this.currentSlide = index;
    }

    // Zoom Methods
    initZoom() {
        this.container.querySelectorAll(this.selectors.zoomImages).forEach(img => {
            img.addEventListener('click', () => this.openLightbox(img));
            img.style.cursor = 'zoom-in';
        });
    }

    openLightbox(img) {
        const zoomUrl = img.dataset.zoom || img.src;

        // Create lightbox
        const lightbox = document.createElement('div');
        lightbox.className = 'product-lightbox';
        lightbox.innerHTML = `
      <div class="product-lightbox__overlay"></div>
      <div class="product-lightbox__content">
        <button class="product-lightbox__close" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" width="24" height="24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img src="${zoomUrl}" alt="">
      </div>
    `;

        document.body.appendChild(lightbox);
        document.body.style.overflow = 'hidden';

        // Close handlers
        const close = () => {
            lightbox.remove();
            document.body.style.overflow = '';
        };

        lightbox.querySelector('.product-lightbox__overlay').addEventListener('click', close);
        lightbox.querySelector('.product-lightbox__close').addEventListener('click', close);
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') close();
        }, { once: true });
    }

    // Sticky Add to Cart Bar
    initStickyBar() {
        const stickyBar = document.querySelector('[data-sticky-bar]');
        const addToCartBtn = this.container.querySelector(this.selectors.addToCartBtn);

        if (!stickyBar || !addToCartBtn) return;

        // Use IntersectionObserver to detect when original button is out of view
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    stickyBar.classList.add('is-visible');
                    stickyBar.setAttribute('aria-hidden', 'false');
                } else {
                    stickyBar.classList.remove('is-visible');
                    stickyBar.setAttribute('aria-hidden', 'true');
                }
            });
        }, {
            threshold: 0,
            rootMargin: '-80px 0px 0px 0px'
        });

        observer.observe(addToCartBtn);

        // Sticky bar add to cart triggers main form
        const stickyAddBtn = stickyBar.querySelector('[data-sticky-add-to-cart]');
        if (stickyAddBtn) {
            stickyAddBtn.addEventListener('click', () => {
                const form = this.container.querySelector(this.selectors.form);
                if (form) {
                    form.dispatchEvent(new Event('submit', { cancelable: true }));
                }
            });
        }
    }

    // Hover Zoom Lens (Desktop only)
    initHoverZoom() {
        // Only on desktop
        if (window.innerWidth < 1024) return;

        const zoomLens = document.querySelector('[data-zoom-lens]');
        if (!zoomLens) return;

        const images = this.container.querySelectorAll('.product-gallery__image[data-zoom]');
        const lensW = 250;
        const lensH = 250;

        images.forEach(img => {
            let cachedRect = null;

            const updateRect = () => { cachedRect = img.getBoundingClientRect(); };

            img.addEventListener('mouseenter', () => {
                updateRect();
                const zoomUrl = img.dataset.zoom || img.src;
                zoomLens.style.cssText += `;background-image:url(${zoomUrl});background-size:${cachedRect.width * 3}px ${cachedRect.height * 3}px`;
                zoomLens.classList.add('is-active');
                img.style.cursor = 'crosshair';
            });

            img.addEventListener('mousemove', (e) => {
                if (!cachedRect) return;

                const x = (e.clientX - cachedRect.left) / cachedRect.width;
                const y = (e.clientY - cachedRect.top) / cachedRect.height;

                let lensX = cachedRect.right + 20;
                let lensY = e.clientY - lensH / 2;

                if (lensX + lensW > window.innerWidth) {
                    lensX = cachedRect.left - lensW - 20;
                }
                lensY = Math.max(10, Math.min(lensY, window.innerHeight - lensH - 10));

                zoomLens.style.left = lensX + 'px';
                zoomLens.style.top = lensY + 'px';
                zoomLens.style.backgroundPosition = `${x * 100}% ${y * 100}%`;
            });

            img.addEventListener('mouseleave', () => {
                cachedRect = null;
                zoomLens.classList.remove('is-active');
            });

            window.addEventListener('resize', updateRect, { passive: true });
            window.addEventListener('scroll', updateRect, { passive: true });
        });
    }

    // Utility Methods
    formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        const [integer, decimals] = amount.split('.');
        const thousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return '€' + thousands + ',' + decimals;
    }

    showNotification(message, type = 'success') {
        // Remove existing notifications
        document.querySelectorAll('.product-notification').forEach(n => n.remove());

        const notification = document.createElement('div');
        notification.className = `product-notification product-notification--${type}`;
        notification.innerHTML = `
      <span>${message}</span>
      <button type="button" aria-label="Close">&times;</button>
    `;

        document.body.appendChild(notification);

        notification.querySelector('button').addEventListener('click', () => notification.remove());

        setTimeout(() => notification.remove(), 5000);
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    const productPage = document.querySelector('[data-product-page]');
    if (productPage) {
        new ProductPage(productPage);
    }
});

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ProductPage;
}
